import { StyledSelect } from "@ando/ui";
import { Bell, ChevronLeft, ChevronRight, RefreshCw, ShieldAlert } from "lucide-react";
import * as React from "react";

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";

import { Badge, Button, cn } from "@ando/ui";

import type {
  AdminNudgeRow,
  AdminNudgeStatus,
  NudgeStatsRow,
  NudgeType,
  NudgeUrgency,
} from "@ando/core";

import { useAdminNudgeStats, useAdminNudges } from "@/features/admin/hooks/useAdminHypermem";
import { formatInt, formatPct, formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmEmpty, HmError, HmLoading } from "./HmStates";

/**
 * HyperMem nudges admin tab (v2 port, Jun 6, 2026) — HIGH PII SENSITIVITY.
 *
 * Cross-tenant observability over the proactive nudges pipeline:
 *   - Statistiche: per-type aggregate panel (last N days, days selector) with
 *     deliver-rate / act-rate / avg-confidence + inline volume bars.
 *   - Lista: paginated rows filterable by status + type, with urgency/status
 *     badges, confidence, server-truncated title + body_preview and timestamps.
 *
 * Privacy posture (GDPR Art. 32 defense-in-depth): the BE redacts ``title`` /
 * ``body_preview`` and never returns the full body. The FE TRUSTS the BE — no
 * client-side re-redaction, no expand button. ``redacted`` surfaces a privacy
 * indicator on the preview cell. Every fetch is server-side audited.
 */

const PAGE_SIZE = 50;

const NUDGE_TYPES_ORDERED: readonly NudgeType[] = [
  "deadline_reminder",
  "forgotten_task",
  "pattern_break",
  "insight",
  "encouragement",
  "support",
];

const NUDGE_TYPE_LABEL_IT: Record<NudgeType, MessageDescriptor> = {
  deadline_reminder: msg`Scadenze`,
  forgotten_task: msg`Task dimenticati`,
  pattern_break: msg`Cambio abitudini`,
  insight: msg`Insight`,
  encouragement: msg`Incoraggiamenti`,
  support: msg`Supporto`,
};

const STATUS_OPTIONS: ReadonlyArray<{
  value: AdminNudgeStatus;
  label: MessageDescriptor;
}> = [
  { value: "pending", label: msg`In attesa` },
  { value: "delivered", label: msg`Consegnati` },
  { value: "snoozed", label: msg`Snooze` },
  { value: "acted", label: msg`Confermati` },
  { value: "dismissed", label: msg`Rifiutati` },
  { value: "expired", label: msg`Scaduti` },
];

const STATUS_LABEL_IT: Record<AdminNudgeStatus, MessageDescriptor> = {
  pending: msg`In attesa`,
  delivered: msg`Consegnato`,
  snoozed: msg`Snooze`,
  acted: msg`Confermato`,
  dismissed: msg`Rifiutato`,
  expired: msg`Scaduto`,
};

const STATUS_BADGE_VARIANT: Record<
  AdminNudgeStatus,
  "default" | "secondary" | "outline" | "success" | "warning" | "destructive"
> = {
  pending: "outline",
  delivered: "default",
  snoozed: "secondary",
  acted: "success",
  dismissed: "secondary",
  expired: "outline",
};

const URGENCY_LABEL_IT: Record<NudgeUrgency, MessageDescriptor> = {
  urgent: msg`Urgente`,
  high: msg`Alta`,
  normal: msg`Normale`,
  low: msg`Bassa`,
};

const URGENCY_BADGE_TONE: Record<NudgeUrgency, string> = {
  urgent: "border-ando-orange/30 bg-ando-orange/15 text-ando-orange",
  high: "border-red-300/70 bg-transparent text-red-700",
  normal: "border-hairline bg-cream-deep text-ink-2",
  low: "border-hairline bg-cream/40 text-ink-3",
};

const RANGE_PRESETS: ReadonlyArray<{ days: number; label: MessageDescriptor }> = [
  { days: 1, label: msg`Ultime 24 h` },
  { days: 7, label: msg`Ultimi 7 g` },
  { days: 30, label: msg`Ultimi 30 g` },
  { days: 90, label: msg`Ultimi 90 g` },
];

type TabId = "list" | "stats";

// ── Stats panel ─────────────────────────────────────────────────────────────

function StatRow({
  type,
  row,
  maxTotal,
}: {
  type: NudgeType;
  row: NudgeStatsRow | undefined;
  maxTotal: number;
}): React.ReactElement {
  const { t, i18n } = useLingui();
  const total = row?.total ?? 0;
  const deliverRate = total > 0 ? ((row?.delivered ?? 0) / total) * 100 : null;
  const actRate = total > 0 ? ((row?.acted ?? 0) / total) * 100 : null;
  const avgConf =
    row && typeof row.avg_confidence === "number" && total > 0
      ? row.avg_confidence.toFixed(2)
      : "—";

  return (
    <li className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="truncate text-ink-1">{i18n._(NUDGE_TYPE_LABEL_IT[type])}</span>
        <span className="shrink-0 font-medium text-ink-1 tabular-nums">
          <Trans>{formatInt(total)} nudge</Trans>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-3/10">
          <div
            className="h-full rounded-full bg-ando-orange/70"
            style={{ width: `${maxTotal ? (total / maxTotal) * 100 : 0}%` }}
          />
        </div>
        <span className="flex shrink-0 items-center gap-3 text-ink-3 text-xs tabular-nums">
          <span title={t`Tasso di consegna`}>
            <Trans>Cons. {formatPct(deliverRate, 0)}</Trans>
          </span>
          <span title={t`Tasso di conferma`}>
            <Trans>Conf. {formatPct(actRate, 0)}</Trans>
          </span>
          <span title={t`Confidenza media`}>
            <Trans>μ {avgConf}</Trans>
          </span>
        </span>
      </div>
    </li>
  );
}

function StatsPanel({ tenantId }: { tenantId: string }): React.ReactElement {
  const { t, i18n } = useLingui();
  const [days, setDays] = React.useState<number>(7);
  const { data, isLoading, isError, refetch, isFetching } = useAdminNudgeStats(tenantId, days);

  const byType = data?.by_type ?? [];
  const maxTotal = byType.reduce((m, r) => Math.max(m, r.total), 0);
  const totalSum = byType.reduce((s, r) => s + r.total, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div
          className="flex items-center gap-1.5"
          role="group"
          aria-label={t`Intervallo temporale`}
        >
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.days}
              type="button"
              onClick={() => setDays(p.days)}
              aria-pressed={days === p.days}
              className={cn(
                "rounded-lg px-3 py-1.5 font-medium text-sm transition-colors",
                days === p.days
                  ? "bg-ando-orange/15 text-ando-orange"
                  : "text-ink-2 hover:bg-ink-3/10",
              )}
            >
              {i18n._(p.label)}
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={cn("mr-1.5 h-3 w-3", isFetching && "animate-spin")} aria-hidden />
          <Trans>Aggiorna</Trans>
        </Button>
      </div>

      {isLoading ? (
        <HmLoading label={t`Caricamento statistiche…`} />
      ) : isError ? (
        <HmError
          message={t`Impossibile caricare le statistiche dei nudge.`}
          onRetry={() => void refetch()}
        />
      ) : totalSum === 0 ? (
        <HmEmpty
          icon={<Bell className="h-8 w-8 opacity-50" aria-hidden />}
          label={t`Nessun nudge nel periodo selezionato`}
        />
      ) : (
        <section className="rounded-xl border border-hairline bg-cream/40 p-4">
          <h4 className="mb-3 font-semibold text-ink-1 text-sm">
            <Trans>Distribuzione per tipologia</Trans>
          </h4>
          <ul className="space-y-3">
            {NUDGE_TYPES_ORDERED.map((t) => (
              <StatRow
                key={t}
                type={t}
                row={byType.find((r) => r.nudge_type === t)}
                maxTotal={maxTotal}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ── List panel ──────────────────────────────────────────────────────────────

function NudgeRow({ item }: { item: AdminNudgeRow }): React.ReactElement {
  const { t, i18n } = useLingui();
  return (
    <tr className="border-hairline border-t align-top">
      <td className="px-3 py-2">
        <code className="text-ink-2 text-xs">{item.user_id}</code>
      </td>
      <td className="px-3 py-2 text-ink-1 text-xs">
        {i18n._(NUDGE_TYPE_LABEL_IT[item.nudge_type])}
      </td>
      <td className="px-3 py-2">
        <Badge variant="outline" className={cn("text-[11px]", URGENCY_BADGE_TONE[item.urgency])}>
          {i18n._(URGENCY_LABEL_IT[item.urgency])}
        </Badge>
      </td>
      <td className="px-3 py-2 text-ink-1 text-xs tabular-nums">{item.confidence.toFixed(2)}</td>
      <td className="px-3 py-2">
        <Badge variant={STATUS_BADGE_VARIANT[item.status]} className="text-[11px]">
          {i18n._(STATUS_LABEL_IT[item.status])}
        </Badge>
      </td>
      <td className="max-w-md px-3 py-2">
        <div className="space-y-0.5">
          <div className="flex items-center gap-1.5">
            <p className="truncate font-medium text-ink-1 text-sm">{item.title}</p>
            {item.redacted ? (
              <span
                className="inline-flex shrink-0 items-center gap-0.5 text-[10px] text-ink-3"
                title={t`Anteprima troncata server-side (PII protetta)`}
              >
                <ShieldAlert className="h-3 w-3" aria-hidden />
                <Trans>anteprima</Trans>
              </span>
            ) : null}
          </div>
          <p className="line-clamp-2 text-ink-3 text-xs">{item.body_preview}</p>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-ink-3 text-xs">
        <time dateTime={item.created_at}>{formatTimestamp(item.created_at)}</time>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-ink-3 text-xs">
        {item.delivered_at ? (
          <time dateTime={item.delivered_at}>{formatTimestamp(item.delivered_at)}</time>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}

function ListPanel({ tenantId }: { tenantId: string }): React.ReactElement {
  const { t, i18n } = useLingui();
  const [statusFilter, setStatusFilter] = React.useState<AdminNudgeStatus | "">("");
  const [typeFilter, setTypeFilter] = React.useState<NudgeType | "">("");
  const [page, setPage] = React.useState(0);

  const opts = React.useMemo(
    () => ({
      status: statusFilter || undefined,
      nudgeType: typeFilter || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [statusFilter, typeFilter, page],
  );

  const { data, isLoading, isError, refetch, isFetching } = useAdminNudges(tenantId, opts);

  const items = data?.items ?? [];
  const hasMore = items.length === PAGE_SIZE;
  const hasFilters = Boolean(statusFilter || typeFilter);

  const selectClass =
    "rounded-lg border border-hairline bg-cream/40 px-2.5 py-1.5 text-sm text-ink-1 focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label htmlFor="hm-nudges-type" className="font-medium text-ink-3 text-xs">
            <Trans>Tipo</Trans>
          </label>
          <StyledSelect
            id="hm-nudges-type"
            className={selectClass}
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as NudgeType | "");
              setPage(0);
            }}
          >
            <option value="">{t`Tutti i tipi`}</option>
            {NUDGE_TYPES_ORDERED.map((nt) => (
              <option key={nt} value={nt}>
                {i18n._(NUDGE_TYPE_LABEL_IT[nt])}
              </option>
            ))}
          </StyledSelect>
        </div>
        <div className="space-y-1">
          <label htmlFor="hm-nudges-status" className="font-medium text-ink-3 text-xs">
            <Trans>Status</Trans>
          </label>
          <StyledSelect
            id="hm-nudges-status"
            className={selectClass}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as AdminNudgeStatus | "");
              setPage(0);
            }}
          >
            <option value="">{t`Tutti gli stati`}</option>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {i18n._(opt.label)}
              </option>
            ))}
          </StyledSelect>
        </div>
        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStatusFilter("");
              setTypeFilter("");
              setPage(0);
            }}
          >
            <Trans>Reset filtri</Trans>
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={cn("mr-1.5 h-3 w-3", isFetching && "animate-spin")} aria-hidden />
          <Trans>Aggiorna</Trans>
        </Button>
      </div>

      {isLoading ? (
        <HmLoading label={t`Caricamento nudge…`} />
      ) : isError ? (
        <HmError message={t`Impossibile caricare i nudge.`} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <HmEmpty
          icon={<Bell className="h-8 w-8 opacity-50" aria-hidden />}
          label={t`Nessun nudge per i filtri selezionati`}
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-hairline bg-cream/40">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="font-medium text-ink-3 text-xs">
                  <th className="px-3 py-2">
                    <Trans>User</Trans>
                  </th>
                  <th className="px-3 py-2">
                    <Trans>Tipo</Trans>
                  </th>
                  <th className="px-3 py-2">
                    <Trans>Urgenza</Trans>
                  </th>
                  <th className="px-3 py-2">
                    <Trans>Conf.</Trans>
                  </th>
                  <th className="px-3 py-2">
                    <Trans>Status</Trans>
                  </th>
                  <th className="px-3 py-2">
                    <Trans>Title / preview</Trans>
                  </th>
                  <th className="px-3 py-2">
                    <Trans>Creato</Trans>
                  </th>
                  <th className="px-3 py-2">
                    <Trans>Consegnato</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <NudgeRow key={item.id} item={item} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-ink-3 text-xs">
            <span>
              <Trans>
                Pagina {page + 1} · {formatInt(items.length)} righe
              </Trans>
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || isFetching}
              >
                <ChevronLeft className="mr-1 h-3 w-3" aria-hidden />
                <Trans>Precedente</Trans>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore || isFetching}
              >
                <Trans>Successiva</Trans>
                <ChevronRight className="ml-1 h-3 w-3" aria-hidden />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────

export function HmNudgesTab({
  tenantId,
}: {
  tenantId: string;
}): React.ReactElement {
  const { t } = useLingui();
  const [tab, setTab] = React.useState<TabId>("list");

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border border-amber-300/70 bg-transparent p-4 text-amber-900 text-sm">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div>
          <p className="font-medium">
            <Trans>Dati sensibili (PII medio) — accesso registrato</Trans>
          </p>
          <p className="mt-0.5 text-amber-800">
            <Trans>
              I nudge contengono testo generato da contenuti utente (titoli email, frammenti chat,
              scadenze documento). Title e body sono troncati server-side e non sono espandibili da
              questa vista. Ogni query è registrata nell'audit trail (GDPR Art. 5(2)).
            </Trans>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5" role="tablist" aria-label={t`Vista nudge`}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "list"}
          onClick={() => setTab("list")}
          className={cn(
            "inline-flex items-center gap-2 rounded-lg px-3 py-1.5 font-medium text-sm transition-colors",
            tab === "list" ? "bg-ando-orange/15 text-ando-orange" : "text-ink-2 hover:bg-ink-3/10",
          )}
        >
          <Bell className="h-4 w-4" aria-hidden />
          <Trans>Lista</Trans>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "stats"}
          onClick={() => setTab("stats")}
          className={cn(
            "inline-flex items-center gap-2 rounded-lg px-3 py-1.5 font-medium text-sm transition-colors",
            tab === "stats" ? "bg-ando-orange/15 text-ando-orange" : "text-ink-2 hover:bg-ink-3/10",
          )}
        >
          <ShieldAlert className="h-4 w-4" aria-hidden />
          <Trans>Statistiche</Trans>
        </button>
      </div>

      {tab === "list" ? <ListPanel tenantId={tenantId} /> : <StatsPanel tenantId={tenantId} />}
    </div>
  );
}

export default HmNudgesTab;
